import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../../hooks/useChat';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import toast from 'react-hot-toast';

const ConversationalAIWorkspace = ({ selectedError, errors, onAnalysisComplete }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [selectedErrorForAnalysis, setSelectedErrorForAnalysis] = useState(null);
  const messagesEndRef = useRef(null);

  const { response, fullResponse, isLoading, error, sendMessage } = useChat(
    'ANTHROPIC',
    'claude-sonnet-4-5-20250929',
    true
  );

  useEffect(() => {
    if (error) {
      toast?.error(error?.message || 'AI analysis failed');
    }
  }, [error]);

  useEffect(() => {
    if (selectedError) {
      setSelectedErrorForAnalysis(selectedError);
      initiateAnalysis(selectedError);
    }
  }, [selectedError]);

  useEffect(() => {
    if (response && !isLoading) {
      setMessages(prev => [
        ...prev?.slice(0, -1),
        { role: 'assistant', content: response, timestamp: new Date()?.toISOString() }
      ]);

      // Save analysis to history
      if (selectedErrorForAnalysis) {
        onAnalysisComplete?.({
          errorId: selectedErrorForAnalysis?.id,
          analysis: response,
          timestamp: new Date()?.toISOString(),
          confidence: 85.4
        });
      }
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const initiateAnalysis = (error) => {
    const systemPrompt = `You are an expert system reliability engineer and root cause analysis specialist. Analyze system errors with deep technical insight, identify root causes, and provide actionable solutions.

Your analysis should include:
1. Root cause identification with technical depth
2. Impact assessment (system-wide implications)
3. Immediate mitigation steps
4. Long-term prevention strategies
5. Related patterns or systemic issues

Be concise, technical, and actionable.`;

    const userPrompt = `Analyze this system error and provide comprehensive root cause analysis:

**Error Details:**
- Type: ${error?.errorType}
- Severity: ${error?.severity}
- Code: ${error?.errorCode}
- Message: ${error?.errorMessage}
- Module: ${error?.affectedModule}
- Occurred: ${new Date(error?.occurredAt)?.toLocaleString()}

**Stack Trace:**
${error?.errorStack || 'Not available'}

**Request Context:**
- URL: ${error?.requestUrl || 'N/A'}
- Method: ${error?.requestMethod || 'N/A'}
- Status: ${error?.responseStatus || 'N/A'}

Provide detailed root cause analysis with actionable recommendations.`;

    const newMessages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ];

    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Analyzing error: ${error?.errorMessage}`, timestamp: new Date()?.toISOString() },
      { role: 'assistant', content: '', timestamp: new Date()?.toISOString(), streaming: true }
    ]);

    sendMessage(newMessages, { temperature: 0.3, max_tokens: 2048 });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!input?.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input, timestamp: new Date()?.toISOString() };
    setMessages(prev => [...prev, userMessage]);

    const conversationHistory = messages?.map(msg => ({
      role: msg?.role,
      content: msg?.content
    }));

    const systemPrompt = {
      role: 'system',
      content: 'You are an expert system reliability engineer. Provide concise, technical answers to follow-up questions about error analysis and system diagnostics.'
    };

    sendMessage([systemPrompt, ...conversationHistory, { role: 'user', content: input }], {
      temperature: 0.3,
      max_tokens: 1024
    });

    setInput('');
    setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: new Date()?.toISOString(), streaming: true }]);
  };

  const handleQuickAction = (action) => {
    const prompts = {
      explain: 'Explain this error in simpler terms for non-technical stakeholders.',
      impact: 'What is the business impact of this error? How many users are affected?',
      prevention: 'What preventive measures can we implement to avoid this error in the future?',
      related: 'Are there any related errors or patterns I should be aware of?'
    };

    setInput(prompts?.[action] || '');
  };

  return (
    <div className="flex flex-col h-[600px]">
      {/* Error Context Banner */}
      {selectedErrorForAnalysis && (
        <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertCircle" className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Analyzing Error</h4>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  {selectedErrorForAnalysis?.severity?.toUpperCase()}
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-1">{selectedErrorForAnalysis?.errorMessage}</p>
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <span className="flex items-center gap-1">
                  <Icon name="Code" className="w-3 h-3" />
                  {selectedErrorForAnalysis?.errorCode}
                </span>
                <span>•</span>
                <span>{selectedErrorForAnalysis?.affectedModule}</span>
                <span>•</span>
                <span>{new Date(selectedErrorForAnalysis?.occurredAt)?.toLocaleString()}</span>
              </div>
            </div>
            <button
              onClick={() => setSelectedErrorForAnalysis(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <Icon name="X" className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
        {messages?.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex p-4 bg-purple-100 rounded-full mb-4">
              <Icon name="MessageSquare" className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Root Cause Analysis</h3>
            <p className="text-gray-600 mb-6">Select an error from the sidebar or ask a question to begin analysis</p>
            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
              {[
                { label: 'Explain Simply', action: 'explain', icon: 'MessageCircle' },
                { label: 'Impact Analysis', action: 'impact', icon: 'TrendingUp' },
                { label: 'Prevention Steps', action: 'prevention', icon: 'Shield' },
                { label: 'Related Patterns', action: 'related', icon: 'GitBranch' }
              ]?.map((item) => (
                <button
                  key={item?.action}
                  onClick={() => handleQuickAction(item?.action)}
                  className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all text-left"
                  disabled={!selectedErrorForAnalysis}
                >
                  <Icon name={item?.icon} className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">{item?.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages?.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${
                message?.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {message?.role === 'assistant' && (
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <Icon name="Brain" className="w-4 h-4 text-white" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-lg p-4 ${
                  message?.role === 'user' ?'bg-purple-600 text-white' :'bg-gray-100 text-gray-900'
                }`}
              >
                <div className="prose prose-sm max-w-none">
                  {message?.streaming && isLoading ? (
                    <div className="flex items-center gap-2">
                      <Icon name="Loader" className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Analyzing...</span>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap">{message?.content}</p>
                  )}
                </div>
                <div className="mt-2 text-xs opacity-70">
                  {new Date(message?.timestamp)?.toLocaleTimeString()}
                </div>
              </div>
              {message?.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <Icon name="User" className="w-4 h-4 text-gray-600" />
                </div>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e?.target?.value)}
          placeholder="Ask a follow-up question about the error analysis..."
          disabled={isLoading}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
        />
        <Button
          type="submit"
          disabled={isLoading || !input?.trim()}
          className="px-6 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
        >
          {isLoading ? (
            <Icon name="Loader" className="w-5 h-5 animate-spin" />
          ) : (
            <Icon name="Send" className="w-5 h-5" />
          )}
        </Button>
      </form>
    </div>
  );
};

export default ConversationalAIWorkspace;